// src/utils/hayllis2SportRedirect.js

/**
 * PURPOSE: Reglas puras de la redirección de un deporte de Legacy a Hayllis 2.0 (FTR-0039).
 * RESPONSIBILITIES: Decidir si el deporte pedido pertenece a un evento configurado (idevent entero, idsport
 *   entero positivo) y no es una de las excepciones de ese evento (idsport que siguen en Legacy), y construir la
 *   URL del mismo evento y deporte en Hayllis 2.0:
 *   {origen}/es/events/{idevent}/sports/{idsport}. Hayllis 2.0 resuelve el evento y el deporte por el id
 *   numérico inicial del parámetro (el slug es decorativo, ADR-0004) y su página de deporte decide la pestaña
 *   inicial. Solo navegación: la visibilidad del evento la sigue decidiendo el backend.
 * COLLABORATORS: src/config/hayllis2SportRedirect.js, src/config/delegatePortal.js (HAYLLIS_2_ORIGIN),
 *   src/pages/Sport.jsx.
 */
import { HAYLLIS_2_ORIGIN } from "../config/delegatePortal.js";
import {
  HAYLLIS_2_SPORT_REDIRECT_EVENT_IDS,
  HAYLLIS_2_SPORT_REDIRECT_LEGACY_SPORT_IDS,
} from "../config/hayllis2SportRedirect.js";

const HAYLLIS_2_LOCALE = "es";

const asPositiveInteger = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

export const shouldRedirectSportToHayllis2 = ({
  idevent,
  idsport,
  eventIds = HAYLLIS_2_SPORT_REDIRECT_EVENT_IDS,
  legacySportIds = HAYLLIS_2_SPORT_REDIRECT_LEGACY_SPORT_IDS,
}) => {
  const eventId = asPositiveInteger(idevent);
  const sportId = asPositiveInteger(idsport);
  if (!eventId || !sportId || !eventIds.includes(eventId)) return false;
  return !(legacySportIds[eventId] ?? []).includes(sportId);
};

export const buildHayllis2SportUrl = ({ origin = HAYLLIS_2_ORIGIN, idevent, idsport }) =>
  `${String(origin || "").replace(/\/+$/, "")}/${HAYLLIS_2_LOCALE}/events/${asPositiveInteger(idevent)}/sports/${asPositiveInteger(idsport)}`;

/** URL de destino si el deporte debe verse en Hayllis 2.0; null si sigue en Legacy. */
export const resolveHayllis2SportRedirect = ({ idevent, idsport, eventIds, legacySportIds, origin } = {}) =>
  shouldRedirectSportToHayllis2({
    idevent,
    idsport,
    ...(eventIds ? { eventIds } : {}),
    ...(legacySportIds ? { legacySportIds } : {}),
  })
    ? buildHayllis2SportUrl({ ...(origin ? { origin } : {}), idevent, idsport })
    : null;
